import React from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import { SERVICES, TIME_SLOTS, PROMO } from "@/lib/data";
import { Check, ArrowRight, ArrowLeft, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ["Service", "Vehicle", "Date & Time", "Details"];

const initialForm = {
  service: "",
  vehicle_year: "",
  vehicle_make: "",
  vehicle_model: "",
  vehicle_type: "",
  appointment_date: null,
  appointment_time: "",
  customer_name: "",
  customer_email: "",
  customer_phone: "",
  address: "",
  city: "",
  notes: "",
};

export const BookingModal = ({ open, onOpenChange, preselectedService, startStep = 0, promo = false }) => {
  const [step, setStep] = React.useState(startStep);
  const [form, setForm] = React.useState(initialForm);
  const [bookedTimes, setBookedTimes] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [success, setSuccess] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      if (preselectedService) {
        setForm((f) => ({ ...f, service: preselectedService }));
      }
      setStep(startStep);
    }
  }, [open, preselectedService, startStep]);

  React.useEffect(() => {
    if (!open) {
      // reset after close
      setTimeout(() => {
        setStep(0);
        setForm(initialForm);
        setSuccess(null);
      }, 300);
    }
  }, [open]);

  // Fetch availability when date selected
  React.useEffect(() => {
    if (form.appointment_date) {
      const dateStr = format(form.appointment_date, "yyyy-MM-dd");
      axios
        .get(`${API}/bookings/availability`, { params: { date: dateStr } })
        .then((r) => setBookedTimes(r.data.booked_times || []))
        .catch(() => setBookedTimes([]));
    }
  }, [form.appointment_date]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canProceed = () => {
    if (step === 0) return !!form.service;
    if (step === 1)
      return (
        form.vehicle_year &&
        form.vehicle_make &&
        form.vehicle_model &&
        form.vehicle_type
      );
    if (step === 2) return !!form.appointment_date && !!form.appointment_time;
    if (step === 3)
      return (
        form.customer_name &&
        form.customer_email &&
        form.customer_phone &&
        form.address &&
        form.city
      );
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const svc = SERVICES.find((s) => s.id === form.service);
      const isPromoEligible = promo && PROMO.enabled && svc.id === PROMO.serviceId;
      const finalPrice = isPromoEligible ? PROMO.promoPrice : svc.price;
      const payload = {
        ...form,
        service_label: svc.label + (isPromoEligible ? " (Promo)" : ""),
        price: finalPrice,
        notes: (form.notes || "") + (isPromoEligible
          ? `\n[PROMO APPLIED] First-25 customers — ${PROMO.promoPrice} instead of ${PROMO.regularPrice}.`
          : ""),
        appointment_date: format(form.appointment_date, "yyyy-MM-dd"),
      };
      const res = await axios.post(`${API}/bookings`, payload);
      toast.success("Booking received! We'll text to confirm shortly.");
      setSuccess(res.data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.[0]?.msg ||
          e?.response?.data?.detail ||
          "Something went wrong. Please call us directly."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden gap-0 max-h-[90vh] overflow-y-auto"
        data-testid="booking-modal"
      >
        <DialogTitle className="sr-only">Book your detail</DialogTitle>
        <DialogDescription className="sr-only">
          Schedule a mobile auto detailing appointment in four steps.
        </DialogDescription>
        {success ? (
          <SuccessView booking={success} onClose={() => onOpenChange(false)} />
        ) : (
          <>
            <div className="p-6 lg:p-8 border-b border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading font-bold text-2xl tracking-tight text-slate-900">
                  Book your detail
                </h2>
                <button
                  onClick={() => onOpenChange(false)}
                  data-testid="booking-close-button"
                  className="p-2 hover:bg-slate-100 rounded-md"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {promo && PROMO.enabled && (
                <div
                  data-testid="booking-promo-banner"
                  className="mb-4 flex items-center gap-3 p-3 rounded-lg bg-[#F3E8FF] border border-[#4B0082]/20"
                >
                  <div className="px-2 py-0.5 rounded-full bg-[#FFD600] text-[#3B0066] text-[10px] font-bold uppercase tracking-widest">
                    Promo
                  </div>
                  <div className="text-sm text-slate-800">
                    <strong className="font-semibold">Full Detail unlocked at {PROMO.promoPrice}</strong>
                    {" "}— first-25 customer pricing applied automatically.
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                {STEPS.map((label, i) => (
                  <React.Fragment key={label}>
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                          i < step
                            ? "bg-[#4B0082] text-white"
                            : i === step
                            ? "bg-[#4B0082] text-white ring-4 ring-[#F3E8FF]"
                            : "bg-slate-200 text-slate-500"
                        }`}
                      >
                        {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                      </div>
                      <span
                        className={`text-xs font-medium hidden sm:inline ${
                          i <= step ? "text-slate-900" : "text-slate-400"
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                    {i < STEPS.length - 1 && (
                      <div className="flex-1 h-px bg-slate-200" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="p-6 lg:p-8 min-h-[340px]">
              {step === 0 && (
                <div data-testid="step-service">
                  <h3 className="font-heading font-semibold text-lg mb-5">
                    Which service?
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {SERVICES.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => update("service", s.id)}
                        data-testid={`select-service-${s.id}`}
                        className={`p-5 text-left rounded-lg border-2 transition-all ${
                          form.service === s.id
                            ? "border-[#4B0082] bg-[#F3E8FF]/40"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className="font-heading font-semibold text-slate-900">
                            {s.label}
                          </span>
                          <span className="font-heading font-bold text-[#4B0082]">
                            {s.price}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">{s.duration}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div data-testid="step-vehicle">
                  <h3 className="font-heading font-semibold text-lg mb-5">
                    Tell us about your vehicle
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="year">Year</Label>
                      <Input
                        id="year"
                        data-testid="vehicle-year-input"
                        placeholder="2021"
                        value={form.vehicle_year}
                        onChange={(e) => update("vehicle_year", e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="make">Make</Label>
                      <Input
                        id="make"
                        data-testid="vehicle-make-input"
                        placeholder="Toyota"
                        value={form.vehicle_make}
                        onChange={(e) => update("vehicle_make", e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="model">Model</Label>
                      <Input
                        id="model"
                        data-testid="vehicle-model-input"
                        placeholder="4Runner"
                        value={form.vehicle_model}
                        onChange={(e) => update("vehicle_model", e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label>Vehicle type</Label>
                      <Select
                        value={form.vehicle_type}
                        onValueChange={(v) => update("vehicle_type", v)}
                      >
                        <SelectTrigger className="mt-1.5" data-testid="vehicle-type-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sedan">Sedan / Coupe</SelectItem>
                          <SelectItem value="suv">SUV / Crossover</SelectItem>
                          <SelectItem value="truck">Truck</SelectItem>
                          <SelectItem value="van">Van / Minivan</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="step-datetime">
                  <h3 className="font-heading font-semibold text-lg mb-5">
                    Pick a date & time
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="flex justify-center md:justify-start">
                      <Calendar
                        mode="single"
                        selected={form.appointment_date}
                        onSelect={(d) => {
                          update("appointment_date", d);
                          update("appointment_time", "");
                        }}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          return date < today;
                        }}
                        className="rounded-md border"
                        data-testid="booking-date-calendar"
                      />
                    </div>
                    <div>
                      <Label className="mb-3 block">Available time slots</Label>
                      {!form.appointment_date && (
                        <p className="text-sm text-slate-500 italic">
                          Select a date first
                        </p>
                      )}
                      {form.appointment_date && (
                        <div className="grid grid-cols-2 gap-2">
                          {TIME_SLOTS.map((t) => {
                            const taken = bookedTimes.includes(t);
                            return (
                              <button
                                key={t}
                                disabled={taken}
                                onClick={() => update("appointment_time", t)}
                                data-testid={`time-slot-${t.replace(/[: ]/g, "")}`}
                                className={`py-2.5 px-3 text-sm font-medium rounded-md border transition-all ${
                                  taken
                                    ? "border-slate-200 text-slate-400 line-through cursor-not-allowed"
                                    : form.appointment_time === t
                                    ? "border-[#4B0082] bg-[#4B0082] text-white"
                                    : "border-slate-200 hover:border-[#4B0082] text-slate-700"
                                }`}
                              >
                                {t}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div data-testid="step-details">
                  <h3 className="font-heading font-semibold text-lg mb-5">
                    Your details
                  </h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Label htmlFor="name">Full name</Label>
                      <Input
                        id="name"
                        data-testid="customer-name-input"
                        value={form.customer_name}
                        onChange={(e) => update("customer_name", e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="customer-email-input"
                        value={form.customer_email}
                        onChange={(e) => update("customer_email", e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="customer-phone-input"
                        placeholder="(801) 555-0123"
                        value={form.customer_phone}
                        onChange={(e) => update("customer_phone", e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="addr">Service address</Label>
                      <Input
                        id="addr"
                        data-testid="customer-address-input"
                        placeholder="Where will we meet your car?"
                        value={form.address}
                        onChange={(e) => update("address", e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        data-testid="customer-city-input"
                        placeholder="e.g. Layton"
                        value={form.city}
                        onChange={(e) => update("city", e.target.value)}
                        className="mt-1.5"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="notes">Notes (optional)</Label>
                      <Textarea
                        id="notes"
                        data-testid="customer-notes-input"
                        placeholder="Pet hair, stains, smoke, etc."
                        value={form.notes}
                        onChange={(e) => update("notes", e.target.value)}
                        className="mt-1.5"
                        rows={3}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 lg:p-8 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
              <Button
                variant="outline"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                data-testid="booking-back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
              </Button>
              {step < STEPS.length - 1 ? (
                <Button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canProceed()}
                  data-testid="booking-form-next-step-button"
                  className="bg-[#4B0082] hover:bg-[#3B0066] text-white"
                >
                  Continue <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              ) : (
                <Button
                  onClick={submit}
                  disabled={!canProceed() || submitting}
                  data-testid="booking-submit-button"
                  className="bg-[#4B0082] hover:bg-[#3B0066] text-white"
                >
                  {submitting ? "Booking..." : "Confirm Booking"}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

const SuccessView = ({ booking, onClose }) => (
  <div className="p-8 lg:p-12 text-center" data-testid="booking-success">
    <div className="w-16 h-16 mx-auto rounded-full bg-[#F3E8FF] flex items-center justify-center mb-6">
      <Check className="w-8 h-8 text-[#4B0082]" />
    </div>
    <h2 className="font-heading font-bold text-3xl tracking-tight text-slate-900 mb-3">
      You're booked!
    </h2>
    <p className="text-slate-600 mb-8 max-w-md mx-auto">
      We've received your request for{" "}
      <strong className="text-slate-900">{booking.service_label}</strong> on{" "}
      <strong className="text-slate-900">
        {format(new Date(booking.appointment_date), "EEEE, MMMM d")}
      </strong>{" "}
      at <strong className="text-slate-900">{booking.appointment_time}</strong>.
      We'll text you within the hour to confirm.
    </p>
    <div className="p-5 bg-slate-50 rounded-lg text-left mb-8 max-w-md mx-auto">
      <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">
        Reference
      </div>
      <div className="font-mono text-sm text-slate-700 break-all">
        {booking.id}
      </div>
    </div>
    <Button
      onClick={onClose}
      data-testid="booking-success-close"
      className="bg-[#4B0082] hover:bg-[#3B0066] text-white"
    >
      Close
    </Button>
  </div>
);
